import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const full = (u) =>
  u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

const CompareSlider = ({ item, index }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <figure data-testid={`transformation-${index}`}>
      <div
        ref={ref}
        data-testid={`transformation-slider-${index}`}
        className="relative aspect-[16/10] cursor-ew-resize touch-none select-none overflow-hidden border border-white/10"
        onPointerDown={(e) => {
          dragging.current = true;
          ref.current.setPointerCapture(e.pointerId);
          update(e.clientX);
        }}
        onPointerMove={(e) => dragging.current && update(e.clientX)}
        onPointerUp={() => (dragging.current = false)}
        onPointerCancel={() => (dragging.current = false)}
      >
        <img src={full(item.after_url)} alt={`${item.title} — after`} draggable={false}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
        <img src={full(item.before_url)} alt={`${item.title} — before`} draggable={false}
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-y-0 w-px bg-volt" style={{ left: `${pos}%` }}>
          <span className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-volt bg-black/80">
            <ChevronsLeftRight className="h-5 w-5 text-volt" />
          </span>
        </div>
        <span className="absolute left-3 top-3 bg-black/70 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">Before</span>
        <span className="absolute right-3 top-3 bg-black/70 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-volt">After</span>
      </div>
      <figcaption className="mt-3 flex items-baseline justify-between gap-4">
        <span className="font-heading text-sm font-bold uppercase tracking-wider">{item.title}</span>
        <span className="shrink-0 text-[10px] uppercase tracking-[0.25em] text-white/40">Drag to compare</span>
      </figcaption>
    </figure>
  );
};

export const BeforeAfter = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/transformations`).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <section id="transformations" data-testid="transformations-section" className="border-t border-white/10 px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
      <Reveal>
        <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-turquoise">04 — Transformations</p>
        <h2 className="font-heading mt-4 max-w-2xl text-3xl font-black uppercase tracking-tight sm:text-4xl lg:text-5xl">
          Raising day to <span className="text-volt">move-in ready</span>
        </h2>
        <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
          Drag the handle on any project to see where it started and where we left it.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2">
        {items.map((item, i) => (
          <Reveal key={item.id} delay={(i % 2) * 0.1}>
            <CompareSlider item={item} index={i} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};
